import React, { useState, useMemo } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, TablePagination, TextField, TableSortLabel, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { enrichAssetsWithGroupSaleInfo, formatAssetDescription, isAssetSold } from '../../../Utils/assetSaleDisplayUtils';
import AssetModalShell from './AssetModalShell/AssetModalShell';
import shell from './AssetModalShell/AssetModalShell.module.scss';

function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) return 1;
    if (a[orderBy] > b[orderBy]) return -1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

const formatDateShort = (d) =>
    !d ? '-' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

const SoldAssetsViewer = ({
    open = false,
    onClose = () => {},
    assets = [],
    assetGroups = [],
    assetRelations = [],
    onRowClick,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('sellDate');
    const [searchTerm, setSearchTerm] = useState('');

    const groupMap = useMemo(() => {
        const map = {};
        (assetGroups || []).forEach(g => { map[g.assetGroupId] = g.name; });
        return map;
    }, [assetGroups]);

    const soldAssets = useMemo(() => {
        const enriched = enrichAssetsWithGroupSaleInfo(assets, assetRelations);
        return enriched.filter((a) => isAssetSold(a));
    }, [assets, assetRelations]);

    const missingAmountCount = useMemo(() => {
        return soldAssets.filter(
            (a) => !a.soldViaAssetGroup && (!a.sellAmount || a.sellAmount === 0)
        ).length;
    }, [soldAssets]);

    const filtered = useMemo(() => {
        if (!searchTerm.trim()) return soldAssets;
        const q = searchTerm.trim().toLowerCase();
        return soldAssets.filter(a => {
            const displayDescription = formatAssetDescription(a.description, a.sellDate, isAssetSold(a));
            return (
            (a.name || '').toLowerCase().includes(q) ||
            (a.assetNumber?.toString() || '').includes(q) ||
            (displayDescription || '').toLowerCase().includes(q) ||
            (groupMap[a.assetGroupId] || '').toLowerCase().includes(q) ||
            formatDateShort(a.sellDate).includes(q)
            );
        });
    }, [soldAssets, searchTerm, groupMap]);

    const sorted = useMemo(() => {
        if (!orderBy) return filtered;
        return stableSort(filtered, getComparator(order, orderBy));
    }, [filtered, order, orderBy]);

    const paginated = useMemo(() => {
        return sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }, [sorted, page, rowsPerPage]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    if (!open) return null;

    const columns = [
        { id: 'assetNumber', label: t('AssetNumber') || 'מספר נכס' },
        { id: 'name', label: t('AssetName') || 'שם נכס' },
        { id: 'assetGroupId', label: t('AssetGroup') || 'קבוצה' },
        { id: 'purchaseDate', label: t('PurchaseDate') || 'תאריך רכישה' },
        { id: 'originalPrice', label: t('OriginalPrice') || 'מחיר מקורי' },
        { id: 'sellDate', label: t('SellDate') || 'תאריך מכירה' },
        { id: 'sellAmount', label: t('SellAmount') || 'סכום מכירה' },
        { id: 'description', label: t('Description') || 'הערות' },
    ];

    return (
        <AssetModalShell
            open={open}
            onClose={onClose}
            title={t('SoldAssets') || 'נכסים שנמכרו'}
            direction={direction}
            size="xl"
            closeOnBackdrop
            toolbar={(
                <div className={shell.toolbar}>
                    <TextField
                        placeholder={t('Search') || 'חיפוש'}
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                        variant="standard"
                        className={shell.searchInput}
                        InputProps={{
                            disableUnderline: true,
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon /></InputAdornment>
                            ),
                        }}
                        inputProps={{ style: { textAlign: direction === 'rtl' ? 'right' : 'left' } }}
                    />
                    <div className={`${shell.stats} ${shell.toolbarEnd}`}>
                        <span className={shell.stat}>
                            {t('TotalSold') || 'סה"כ נמכרו'}: <strong>{soldAssets.length}</strong>
                        </span>
                        {missingAmountCount > 0 && (
                            <span className={`${shell.stat} ${shell.statWarning}`}>
                                <WarningAmberIcon />
                                {t('MissingSellAmount') || 'חסר סכום מכירה'}: <strong>{missingAmountCount}</strong>
                            </span>
                        )}
                    </div>
                </div>
            )}
            footer={(
                <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                    {t('Close') || 'סגור'}
                </button>
            )}
        >
            <div className={shell.tableWrap}>
                <TableContainer sx={{ maxHeight: '55vh' }}>
                    <Table stickyHeader size="small" dir={direction}>
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell key={col.id} align="center">
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((asset) => {
                                const isLinkedChildSale = Boolean(asset.soldViaAssetGroup);
                                const isMissing = !isLinkedChildSale && (!asset.sellAmount || asset.sellAmount === 0);
                                const displayDescription = formatAssetDescription(
                                    asset.description,
                                    asset.sellDate,
                                    isAssetSold(asset)
                                );
                                return (
                                    <TableRow
                                        key={asset.id}
                                        hover
                                        onClick={() => onRowClick?.(asset.id, 'asset')}
                                        className={`${shell.rowClickable}${isMissing ? ` ${shell.rowDanger}` : ''}`}
                                    >
                                        <TableCell align="center">{asset.assetNumber ?? '-'}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600 }}>{asset.name ?? '-'}</TableCell>
                                        <TableCell align="center">{groupMap[asset.assetGroupId] || '-'}</TableCell>
                                        <TableCell align="center">{formatDateShort(asset.purchaseDate)}</TableCell>
                                        <TableCell align="center">{beautifyNumber(asset.originalPrice, 0)}</TableCell>
                                        <TableCell align="center">{formatDateShort(asset.sellDate)}</TableCell>
                                        <TableCell align="center">
                                            {isLinkedChildSale ? (
                                                '-'
                                            ) : isMissing ? (
                                                <Tooltip title={t('MissingSellAmount') || 'סכום מכירה חסר'} arrow>
                                                    <span className={`${shell.badge} ${shell.badgeDanger}`}>
                                                        <WarningAmberIcon sx={{ fontSize: 14, marginInlineEnd: '4px' }} />
                                                        {t('Missing') || 'חסר'}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <strong>{beautifyNumber(asset.sellAmount, 0)}</strong>
                                            )}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: 'var(--xtram-mirage-muted)', fontSize: 12 }}>{displayDescription || '-'}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {paginated.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>
                                        <div className={shell.emptyState}>{t('NoSoldAssets') || 'אין נכסים שנמכרו'}</div>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    className={shell.pagination}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={sorted.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    labelRowsPerPage={t('RowsPerPage') || 'שורות בעמוד'}
                />
            </div>
        </AssetModalShell>
    );
};

export default SoldAssetsViewer;
